const { h, render } = window.preact;
const { useState, useEffect, useRef } = window.preactHooks;
const html = window.htm.bind(h);

import { RollenfinderStep1 } from "./rollenfinder-step-1.js";
import { RollenfinderStep2 } from "./rollenfinder-step-2.js";
import { RollenfinderStep3 } from "./rollenfinder-step-3.js";
import {
  RollenfinderProvider,
  useRollenfinder,
} from "./rollenfinder-context.js";
import { getTranslation } from "./translations.js";

function RollenfinderContent() {
  const { state } = useRollenfinder();
  const [isOpen, setIsOpen] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const stepsContainerRef = useRef(null);

  const [steps, setSteps] = useState([
    {
      step: 1,
      title: getTranslation("steps.step_1.title"),
      active: true,
      completed: false,
    },
    {
      step: 2,
      title: getTranslation("steps.step_2.title"),
      active: false,
      completed: false,
    },
    {
      step: 3,
      title: getTranslation("steps.step_3.title"),
      active: false,
      completed: false,
    },
  ]);

  // Add effect to center active step
  useEffect(() => {
    const centerActiveStep = () => {
      if (!stepsContainerRef.current) return;

      const container = stepsContainerRef.current;
      const activeStep = container.querySelector('[data-active="true"]');

      if (!activeStep) return;

      const containerWidth = container.offsetWidth;
      const stepWidth = activeStep.offsetWidth;
      const stepLeft = activeStep.offsetLeft;

      // Calculate the scroll position to center the active step
      const scrollPosition = stepLeft - (containerWidth - stepWidth) / 2;

      // Use scrollLeft for immediate positioning
      container.scrollLeft = scrollPosition;
    };

    // Initial centering
    centerActiveStep();

    // Add a slight delay to ensure proper centering after animations
    const timeoutId = setTimeout(centerActiveStep, 150);
    return () => clearTimeout(timeoutId);
  }, [steps]); // Re-run when steps change

  // Reset products display when starting a new search
  useEffect(() => {
    const handleReset = () => {
      setSteps([
        {
          step: 1,
          title: getTranslation("steps.step_1.title"),
          active: true,
          completed: false,
        },
        {
          step: 2,
          title: getTranslation("steps.step_2.title"),
          active: false,
          completed: false,
        },
        {
          step: 3,
          title: getTranslation("steps.step_3.title"),
          active: false,
          completed: false,
        },
      ]);
      setValidationError(null);
    };

    window.addEventListener("rollenfinder:reset", handleReset);
    return () => {
      window.removeEventListener("rollenfinder:reset", handleReset);
    };
  }, []);

  // Check if a step is currently valid
  const isStepValid = (stepNumber) => {
    switch (stepNumber) {
      case 1:
        return state.selectedManufacturers.length > 0;
      case 2:
        return state.selectedModel !== null;
      case 3:
        // Add validation for special requirements if needed
        return true;
      default:
        return false;
    }
  };

  // Only remove completion status if requirements are no longer met
  const updateStepsCompletion = () => {
    setSteps(
      steps.map((step) => ({
        ...step,
        completed: step.completed ? isStepValid(step.step) : step.completed,
      })),
    );
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1:
        if (!state.selectedManufacturers.length) {
          setValidationError(getTranslation("validation.select_manufacturer"));
          return false;
        }
        break;
      case 2:
        if (!state.selectedModel) {
          setValidationError(getTranslation("validation.select_model"));
          return false;
        }
        break;
      case 3:
        // Add validation for special requirements if needed
        break;
    }
    setValidationError(null);
    return true;
  };

  const handleStepClick = (step) => {
    // Only validate when moving forward
    const currentStep = steps.find((s) => s.active);
    if (step.step > currentStep.step && !validateStep(currentStep.step)) {
      return;
    }

    setSteps(
      steps.map((s) => ({
        ...s,
        active: s.step === step.step,
      })),
    );
  };

  const handleNext = () => {
    const currentStepIndex = steps.findIndex((s) => s.active);
    const currentStep = steps[currentStepIndex];

    if (!validateStep(currentStep.step)) {
      return;
    }

    // Mark current step as completed when moving forward
    if (currentStepIndex < steps.length - 1) {
      setSteps(
        steps.map((s, index) => ({
          ...s,
          active: index === currentStepIndex + 1,
          completed: index === currentStepIndex ? true : s.completed,
        })),
      );
      setValidationError(null);
    }
  };

  const handlePrevious = () => {
    const currentStepIndex = steps.findIndex((s) => s.active);
    if (currentStepIndex > 0) {
      setSteps(
        steps.map((s, index) => ({
          ...s,
          active: index === currentStepIndex - 1,
          completed: s.completed && index !== currentStepIndex,
        })),
      );
      setValidationError(null);
    }
  };

  // Only check for invalidation of completed steps
  useEffect(() => {
    updateStepsCompletion();
  }, [state.selectedManufacturers, state.selectedModel]);

  const currentStep = steps.find((s) => s.active);
  const isFirstStep = currentStep.step === 1;
  const isLastStep = currentStep.step === steps.length;
  const isStep3 = currentStep.step === 3;

  // Handle modal open/close effects
  useEffect(() => {
    if (isOpen) {
      // Store the current overflow style
      const originalOverflow = document.body.style.overflow;

      // Only set overflow if cart drawer is not open
      const cartDrawer = document.querySelector("#js-cart-drawer");
      if (!cartDrawer || cartDrawer.classList.contains("hidden")) {
        document.body.style.overflow = "hidden";
      }

      // Cleanup function
      return () => {
        // Only restore overflow if cart drawer is not open
        if (!cartDrawer || cartDrawer.classList.contains("hidden")) {
          document.body.style.overflow = originalOverflow;
        }
      };
    }
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Ignore clicks on modal buttons and content
      if (
        e.target.closest("button") ||
        e.target.closest(".rollenfinder-modal-content")
      ) {
        return;
      }

      // Only handle clicks on the backdrop
      if (e.target.closest(".rollenfinder-modal-backdrop")) {
        closeModal();
      }
    };

    if (isOpen) {
      // Use a short timeout to prevent immediate closing
      setTimeout(() => {
        document.addEventListener("click", handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen]);

  // Hash navigation support
  useEffect(() => {
    // Check URL hash on component mount
    const checkInitialHash = () => {
      if (window.location.hash === '#rollenfinder') {
        setIsOpen(true);
      }
    };

    // Listen for hash changes
    const handleHashChange = () => {
      if (window.location.hash === '#rollenfinder') {
        setIsOpen(true);
      } else if (window.location.hash !== '#rollenfinder' && isOpen) {
        setIsOpen(false);
      }
    };

    // Check initial hash
    checkInitialHash();

    // Add hash change listener
    window.addEventListener('hashchange', handleHashChange);

    // Cleanup
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [isOpen]);

  // Sync modal state with URL hash
  useEffect(() => {
    if (isOpen && window.location.hash !== '#rollenfinder') {
      // Modal opened but hash not set - add hash without triggering scroll
      history.pushState(null, '', '#rollenfinder');
    } else if (!isOpen && window.location.hash === '#rollenfinder') {
      // Modal closed but hash still set - remove hash
      history.pushState(null, '', window.location.pathname + window.location.search);
    }
  }, [isOpen]);

  // Prevent browser scroll to #rollenfinder element (since it doesn't exist)
  useEffect(() => {
    const preventHashScroll = (e) => {
      if (window.location.hash === '#rollenfinder') {
        e.preventDefault();
        // Small timeout to ensure hash is processed first
        setTimeout(() => {
          window.scrollTo({ top: window.scrollY, behavior: 'instant' });
        }, 0);
      }
    };

    window.addEventListener('hashchange', preventHashScroll);
    
    return () => {
      window.removeEventListener('hashchange', preventHashScroll);
    };
  }, []);

  // Helper function to close modal and remove hash
  const closeModal = () => {
    setIsOpen(false);
    // Ensure hash is removed immediately
    if (window.location.hash === '#rollenfinder') {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  };

  return html`
    <div>

      ${isOpen &&
      html`
        <div
          class="rollenfinder-modal relative z-50"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div
            class="rollenfinder-modal-backdrop fixed inset-0 transition-opacity"
            aria-hidden="true"
          ></div>

          <div class="fixed inset-0 z-20 w-screen overflow-y-auto">
            <div
              class="flex min-h-full items-center justify-center bg-black/50 p-2 text-center md:items-center md:p-0"
            >
              <div
                class="rollenfinder-modal-content relative flex h-[80vh] w-full transform flex-col overflow-hidden rounded-lg bg-bg text-left transition-all md:max-h-[70vh] md:max-page-width mx-2 max-w-[1200px]"
                onClick=${(e) => e.stopPropagation()}
              >
                <div class="flex h-full flex-col">
                  <div
                    class="sticky top-0 z-10 bg-bg px-4 pt-4 md:px-[60px] md:pt-12"
                  >
                    <button
                      onClick=${closeModal}
                      class="absolute top-4 right-4 cursor-pointer rounded-full p-2 transition-colors "
                      aria-label=${getTranslation("close_modal")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        viewBox="0 0 22 22"
                        fill="none"
                        class="text-gray-500"
                        aria-hidden="true"
                      >
                        <path
                          d="M1.5 1.5L20.5 20.5"
                          stroke="currentColor"
                          stroke-width="2"
                        />
                        <path
                          d="M20.5 1.5L1.5 20.5"
                          stroke="currentColor"
                          stroke-width="2"
                        />
                      </svg>
                    </button>
                    <div class="flex flex-col gap-4 md:gap-10">
                      <span class="md:text-h3 text-xl font-extrabold"
                        >${getTranslation("title")}</span
                      >
                      <div
                        class="border-borders relative grid grid-cols-3 border-b pb-4 sm:flex sm:w-full"
                      >
                        <div
                          ref=${stepsContainerRef}
                          class="flex gap-4 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] md:hidden [&::-webkit-scrollbar]:hidden"
                        >
                          ${steps.map(
                            (step) => html`
                              <button
                                data-active=${step.active}
                                class="flex min-w-max cursor-pointer flex-row items-center justify-center gap-1"
                                onClick=${() => handleStepClick(step)}
                              >
                                <span
                                  class="${step.active &&
                                  "font-extrabold text-primary"} text-sm md:text-base"
                                  >${step.step}.</span
                                >
                                <span
                                  class="${step.active &&
                                  "font-extrabold text-primary"} flex items-center gap-1 text-sm whitespace-nowrap md:text-base"
                                  >${step.title}${step.completed
                                    ? html`
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          stroke-width="3"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                          class="text-success size-4 flex-shrink-0 md:size-5"
                                        >
                                          <path d="M20 6L9 17l-5-5" />
                                        </svg>
                                      `
                                    : ""}</span
                                >
                              </button>
                            `,
                          )}
                        </div>
                        <div
                          class="hidden gap-4 md:grid md:grid-cols-3 lg:flex lg:w-full lg:justify-between"
                        >
                          ${steps.map(
                            (step) => html`
                              <button
                                class="flex cursor-pointer flex-row items-center justify-center gap-1"
                                onClick=${() => handleStepClick(step)}
                              >
                                <span
                                  class="${step.active &&
                                  "font-extrabold text-primary"} text-base"
                                  >${step.step}.</span
                                >
                                <span
                                  class="${step.active &&
                                  "font-extrabold text-primary"} flex items-center gap-1 text-base"
                                  >${step.title}${step.completed
                                    ? html`
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          stroke-width="3"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                          class="text-success size-5 flex-shrink-0"
                                        >
                                          <path d="M20 6L9 17l-5-5" />
                                        </svg>
                                      `
                                    : ""}</span
                                >
                              </button>
                            `,
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="flex-1 overflow-x-hidden overflow-y-auto px-4 md:px-[60px]"
                  >
                    <div class="py-6">
                      ${steps[0].active &&
                      html`<${RollenfinderStep1} error=${validationError} />`}
                      ${steps[1].active && html`<${RollenfinderStep2} />`}
                      ${steps[2].active && html`<${RollenfinderStep3} />`}
                    </div>
                  </div>
                  <div
                    class="border-borders sticky bottom-0 flex flex-row justify-end gap-4 border-t bg-white px-4 py-2 md:px-[60px]"
                  >
                    <div class="flex gap-2 md:gap-4">
                      ${!isFirstStep &&
                      html`
                        <button
                          type="button"
                          onClick=${handlePrevious}
                          class="bg-bg text-primary-text hover:bg-disabled button-label focus-visible:ring-primary inline-flex h-10 cursor-pointer items-center justify-center rounded-md px-3 py-2 text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:outline-none md:px-4 md:text-base"
                        >
                          ${getTranslation("navigation.previous")}
                        </button>
                      `}
                      ${isFirstStep &&
                      html`
                        <button
                          type="button"
                          onClick=${closeModal}
                          class="bg-bg text-primary-text hover:bg-disabled button-label focus-visible:ring-primary inline-flex h-10 cursor-pointer items-center justify-center rounded-md px-3 py-2 text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:outline-none md:px-4 md:text-base"
                        >
                          ${getTranslation("navigation.cancel")}
                        </button>
                      `}
                      ${!isLastStep &&
                      html`
                        <button
                          type="button"
                          onClick=${handleNext}
                          class="bg-primary hover:bg-primary-hover active:bg-primary-pressed button-label focus-visible:ring-primary inline-flex h-10 cursor-pointer items-center justify-center rounded-md px-3 py-2 text-sm font-semibold text-white transition-colors focus-visible:ring-2 focus-visible:outline-none md:px-4 md:text-base"
                        >
                          ${getTranslation("navigation.next")}
                        </button>
                      `}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `}
    </div>
  `;
}

function RollenfinderWrapper() {
  return html`
    <${RollenfinderProvider}>
      <${RollenfinderContent} />
    <//>
  `;
}

// Wait for DOM to be ready
function initRollenfinder() {
  // Check for local container first (e.g., on collection pages), then global container
  let container = document.getElementById("rollenfinder-container");
  if (!container) {
    container = document.getElementById("rollenfinder-global-container");
  }
  
  if (!container) {
    console.error("Rollenfinder container not found");
    return;
  }

  // Show container since rollenfinder is being initialized
  container.classList.remove("hidden");

  // Render the Rollenfinder
  render(html`<${RollenfinderWrapper} />`, container);
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initRollenfinder);
} else {
  initRollenfinder();
}
